import { Children, cloneElement, isValidElement, memo, type ReactNode, useState } from 'react';
import { useRadioGroupState } from 'react-stately';
import cn from 'classnames';
import { Button, HStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import type { Nullable } from '@direct-frontend/stdlib';
import { RadioGroupContext } from './lib/RadioGroupContext';
import { type VariantInfoDisplayType, VariantInfoDisplayTypes } from '../../api/variants';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import styles from './RadioGroup.module.css';

export interface RadioGroupProps extends TestProps {
    value?: Nullable<string>;
    onChange?: (value: string) => void;
    isError?: boolean;
    isDisabled?: boolean;
    children: ReactNode;
    className?: string;
    'aria-label'?: string;
    name?: string;
    maxVisibleItems?: number;
    disableCollapse?: boolean;
    isWrappable?: boolean;
    displayType?: VariantInfoDisplayType;
}

const childModifier = makeModifierClassname(styles, 'child');
const toggleButtonModifier = makeModifierClassname(styles, 'toggleButton');

export const RadioGroup = memo(function RadioGroup({
    'data-testid': dataTestId = 'RadioGroup',
    value,
    onChange,
    isDisabled = false,
    isError = false,
    children,
    className,
    name,
    maxVisibleItems = 8,
    disableCollapse = false,
    isWrappable = false,
    displayType,
    'aria-label': ariaLabel,
}: RadioGroupProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const contextValue = useRadioGroupState({
        value,
        onChange,
        isDisabled,
        name,
    });

    const childrenArray = Children.toArray(children);
    const hasMoreItems = !disableCollapse && childrenArray.length > maxVisibleItems;

    const hiddenCount = hasMoreItems ? childrenArray.length - maxVisibleItems : 0;

    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <RadioGroupContext.Provider value={contextValue}>
            <div
                data-testid={dataTestId}
                role="radiogroup"
                aria-label={ariaLabel}
                className={cn(styles.radioGroup, className)}
            >
                <HStack
                    flexWrap={isWrappable ? 'wrap' : 'nowrap'}
                    gap="8"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    className={styles.items}
                >
                    {childrenArray.map((child, index) => {
                        const isHidden = hasMoreItems && !isExpanded && index >= maxVisibleItems;

                        return isValidElement(child)
                            ? cloneElement(child, {
                                  key: index,
                                  isError: child.props.isError || isError,
                                  isDisabled: child.props.isDisabled || isDisabled,
                                  dataTestId: `${dataTestId}.Element`,
                                  className: cn(
                                      child.props.className,
                                      childModifier.className,
                                      childModifier('hidden', isHidden)
                                  ),
                              })
                            : null;
                    })}
                    {hasMoreItems && !isExpanded && (
                        <Button
                            onClick={handleToggleExpand}
                            className={cn(
                                toggleButtonModifier.className,
                                toggleButtonModifier('circle', displayType === VariantInfoDisplayTypes.COLOR),
                                toggleButtonModifier('image', displayType === VariantInfoDisplayTypes.IMAGE)
                            )}
                            color="text"
                            size="xs"
                            aria-expanded={false}
                            aria-label={`Показать еще ${hiddenCount} вариантов`}
                            data-testid={`${dataTestId}.More`}
                        >
                            +{hiddenCount}
                        </Button>
                    )}

                    {hasMoreItems && isExpanded && (
                        <Button
                            onClick={handleToggleExpand}
                            className={cn(
                                toggleButtonModifier.className,
                                toggleButtonModifier('circle', displayType === VariantInfoDisplayTypes.COLOR),
                                toggleButtonModifier('image', displayType === VariantInfoDisplayTypes.IMAGE)
                            )}
                            aria-expanded
                            color="text"
                            size="xs"
                            aria-label="Скрыть дополнительные варианты"
                            data-testid={`${dataTestId}.Hide`}
                        >
                            Скрыть
                        </Button>
                    )}
                </HStack>
            </div>
        </RadioGroupContext.Provider>
    );
});
